import { machines } from '../../content/machines'
import { site } from '../../content/site'
import { blank, line, plain, span, text } from './format'
import { markdownLines } from './markdown'
import { contactLines, linkLines, machineLines, projectLines } from './render'
import type { Line, ShellState } from './types'

export type FsFile = {
  kind: 'file'
  name: string
  mode: string
  mtime: Date
  render: (state: ShellState) => Line[]
}

export type FsDir = {
  kind: 'dir'
  name: string
  mode: string
  mtime: Date
  children: FsNode[]
}

export type FsNode = FsFile | FsDir

/** Matches the `mkultra69@cadillac` header in the captured fastfetch output. */
export const USER = 'mkultra69'
export const HOME = `/home/${USER}`

/**
 * Fixed epoch so `ls -l` prints stable timestamps instead of shifting on every
 * render. It matches the portfolio snapshot date the seed data was taken on.
 */
const EPOCH = new Date(2026, 6, 27, 3, 14)
const at = (minutes: number) => new Date(EPOCH.getTime() + minutes * 60_000)

const file = (
  name: string,
  render: (state: ShellState) => Line[],
  options: { mode?: string; mtime?: Date } = {},
): FsFile => ({
  kind: 'file',
  name,
  mode: options.mode ?? '-rw-r--r--',
  mtime: options.mtime ?? EPOCH,
  render,
})

const dir = (name: string, children: FsNode[], mtime = EPOCH): FsDir => ({
  kind: 'dir',
  name,
  mode: 'drwxr-xr-x',
  mtime,
  children,
})

const zshrc = (): Line[] => [
  text('# ~/.zshrc', 'dim'),
  text(`export ZSH="$HOME/.oh-my-zsh"`),
  line(span('ZSH_THEME='), span('"robbyrussell"', 'str')),
  line(span('plugins=('), span('git', 'exec'), span(' '), span('zsh-autosuggestions', 'exec'), span(')')),
  text('source $ZSH/oh-my-zsh.sh'),
  blank(),
  line(span('alias', 'key'), span(' ff='), span('"fastfetch"', 'str')),
  line(span('alias', 'key'), span(' ll='), span('"ls -la"', 'str')),
]

const osRelease = (): Line[] =>
  [
    'PRETTY_NAME="Arch Linux"',
    'NAME="Arch Linux"',
    'ID=arch',
    'BUILD_ID=rolling',
    'HOME_URL="https://mk69.dev/"',
  ].map((value) => {
    const split = value.indexOf('=')
    return line(span(value.slice(0, split), 'key'), span('='), span(value.slice(split + 1), 'str'))
  })

const motd = (): Line[] => [
  text(site.name, 'accent'),
  text(site.bio, 'dim'),
]

/** The whole tree is rebuilt per command; it is a few dozen lazy nodes. */
export function buildTree(state: ShellState): FsDir {
  const projectFiles = state.projects.map((project, index) =>
    file(`${project.slug}.md`, (current) => projectLines(project, current.locale), {
      mtime: at(index * 7),
    }),
  )
  const machineFiles = machines.map((machine, index) =>
    file(`${machine.slug}.txt`, () => machineLines(machine.slug), { mtime: at(60 + index * 11) }),
  )

  /*
   * The prose files come from the CRM, so the directory listing follows
   * whatever the operator publishes rather than a hard-coded set. If every
   * document is gone the two generated files stand in, so `contact` and
   * `links` never answer with an empty home directory.
   */
  const documentFiles = state.documents.length
    ? state.documents.map((document, index) =>
        file(document.name, (current) => markdownLines(document.body[current.locale]), {
          mtime: at(3 + index * 2),
        }),
      )
    : [
        file('contact.txt', (current) => contactLines(current.locale), { mtime: at(3) }),
        file('links.txt', linkLines, { mtime: at(5) }),
      ]

  const home = dir(USER, [
    file('.zshrc', zshrc, { mtime: at(-2_880) }),
    ...documentFiles,
    dir('machines', machineFiles, at(60)),
    dir('projects', projectFiles, at(1)),
  ])

  return dir('/', [
    dir('etc', [
      file('hostname', () => [text('cadillac')]),
      file('motd', motd),
      file('os-release', osRelease),
    ]),
    dir('home', [home]),
  ])
}

/** Normalises `.`, `..`, `~` and relative segments into an absolute path. */
export function resolvePath(input: string, cwd: string, home: string): string {
  let value = input.trim()
  if (!value) return cwd
  if (value === '~') value = home
  else if (value.startsWith('~/')) value = `${home}${value.slice(1)}`

  const stack = value.startsWith('/') ? [] : cwd.split('/').filter(Boolean)
  for (const part of value.split('/')) {
    if (!part || part === '.') continue
    if (part === '..') stack.pop()
    else stack.push(part)
  }
  return stack.length ? `/${stack.join('/')}` : '/'
}

export function lookup(root: FsDir, path: string): FsNode | undefined {
  if (path === '/') return root
  let node: FsNode = root
  for (const part of path.split('/').filter(Boolean)) {
    if (node.kind !== 'dir') return undefined
    const next: FsNode | undefined = node.children.find((child) => child.name === part)
    if (!next) return undefined
    node = next
  }
  return node
}

/** Byte count of the rendered file, used by `ls -l` and `wc -c`. */
export const fileSize = (node: FsFile, state: ShellState): number =>
  node.render(state).reduce((total, row) => total + plain(row).length + 1, 0)

/**
 * Filesystem completions for the token under the cursor. Directories keep their
 * trailing slash so a second Tab descends instead of re-completing.
 */
export function completePath(prefix: string, state: ShellState, dirsOnly = false): string[] {
  const slash = prefix.lastIndexOf('/')
  const base = slash < 0 ? '' : prefix.slice(0, slash + 1)
  const tail = slash < 0 ? prefix : prefix.slice(slash + 1)
  const parent = lookup(buildTree(state), resolvePath(base || '.', state.cwd, state.env.HOME))
  if (!parent || parent.kind !== 'dir') return []
  return parent.children
    .filter((child) => !dirsOnly || child.kind === 'dir')
    .filter((child) => child.name.startsWith(tail))
    .filter((child) => tail.startsWith('.') || !child.name.startsWith('.'))
    .map((child) => `${base}${child.name}${child.kind === 'dir' ? '/' : ''}`)
}
